import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { KeenIcon } from '@/components';
import MediaUploadModal from '@/pages/habor-management/MediaUploadModal';
import { ImageCaptionDTO } from '@/types/api.types';

interface PierImageInputProps {
  value: File | string | null | undefined;
  captions?: ImageCaptionDTO[];
  onChange: (media: File | string, captions: ImageCaptionDTO[]) => void;
  onRemove: () => void;
  disabled?: boolean;
  /** Upload/remove or the parent form's save is in progress — shows a spinner and blocks interaction */
  loading?: boolean;
  error?: string;
}

const PierImageInput = ({
  value,
  captions,
  onChange,
  onRemove,
  disabled,
  loading,
  error,
}: PierImageInputProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const isDisabled = disabled || loading;

  useEffect(() => {
    if (value instanceof File) {
      const objectUrl = URL.createObjectURL(value);
      setPreviewUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
    setPreviewUrl(value || null);
    return undefined;
  }, [value]);

  return (
    <div className="flex flex-col items-start gap-2">
      {previewUrl && (
        <div className="relative inline-block max-w-[280px]">
          <img
            src={previewUrl}
            alt="Pier layout preview"
            className="max-w-full h-auto rounded-lg"
          />
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 contrast:bg-black/60">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500" />
            </div>
          )}
          {!isDisabled && (
            <button
              type="button"
              onClick={onRemove}
              className="absolute top-1 right-1 bg-red-500 contrast:bg-red-800 text-white rounded-full w-6 h-6 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-red-500"
              aria-label="Remove pier image"
            >
              <KeenIcon type="filled" icon="cross" className="text-sm" />
            </button>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        disabled={isDisabled}
        className={clsx(
          'py-2 px-4 rounded-lg border-0 text-sm font-semibold bg-blue-50 dark:bg-blue-900 contrast:bg-blue-900 text-blue-700 dark:text-blue-200 contrast:text-blue-100 hover:bg-blue-100 dark:hover:bg-blue-800 contrast:hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500',
          isDisabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        {previewUrl ? 'Replace Image' : 'Upload Pier Image'}
      </button>
      <p className="text-gray-500 text-xs mt-1 contrast:text-white/70">
        Recommended: 4:3 aspect ratio (e.g. 1024 × 768 px)
      </p>
      {error && (
        <p className="text-red-500 text-xs mt-1" role="alert">
          {error}
        </p>
      )}
      <MediaUploadModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={(media, savedCaptions) => {
          const selected = media[0];
          if (selected) {
            onChange(selected, savedCaptions);
          }
        }}
        allowedTypes={['image/jpeg', 'image/jpg', 'image/png']}
        maxFiles={1}
        initialMedia={value ? [value] : []}
        initialCaptions={captions || []}
        guidelinesResolution="Recommended: 4:3 aspect ratio (e.g. 1024 × 768 px)"
      />
    </div>
  );
};

export { PierImageInput };
